import { IWallClock } from '../../utils/time/IWallClock';
import { DateTime } from 'luxon';
import {
  ServerCookies,
  WriteableServerCookies,
} from '../../utils/serverCookies';

export enum AuthTokenErrorType {
  NEEDS_REFRESH = 'NEEDS_REFRESH',
  REFRESH_FAILED = 'REFRESH_FAILED',
  NO_TOKEN = 'NO_TOKEN',
  NOT_INITIALIZED = 'NOT_INITIALIZED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  REFRESH_BACKOFF = 'REFRESH_BACKOFF',
  MAX_ATTEMPTS_EXCEEDED = 'MAX_ATTEMPTS_EXCEEDED',
  OTHER = 'OTHER',
}

export type AuthTokenSource =
  | 'bruntwoodSession'
  | 'bruntwoodToken'
  | 'nexudusToken'
  | 'nexudusRefreshToken';

export enum AuthTokenErrorAction {
  RETRY = 'RETRY',
  LOGOUT = 'LOGOUT',
  SHOW_ERROR = 'SHOW_ERROR',
  SUCCESS_CONTINUE = 'SUCCESS_CONTINUE',
}

export interface IAuthTokenError {
  source: AuthTokenSource;

  type: AuthTokenErrorType;
  message: string;
}

export interface ISerializedAuthToken<Meta = any> {
  source: AuthTokenSource;
  token: string | null;
  expiryDate: string | null;

  /**
   * Defines data that is specific to this token - for example the nexudus token would have the
   * coworkerId here.
   */
  meta: Meta;
}

export type ISerializedAuthTokens = Record<
  AuthTokenSource,
  ISerializedAuthToken
>;

export interface IAuthTokenData {
  source: AuthTokenSource;
  value: string;
  expiryDate: DateTime | null;
}

/**
 * Tokens are able to load themselves using other tokens as dependencies.
 *
 * For example, a bruntwood token can be used to get a nexudus token.
 */
export type TokenDependencies = Partial<
  Record<AuthTokenSource, IAuthTokenData>
>;

/**
 * For internal use only - will be used to manage the token store
 */
export interface IAuthTokenStore<Meta = any> {
  source: AuthTokenSource;

  expiryDate: DateTime | null;

  value: string | null;

  subscriber: string | null;

  meta: Meta;

  isReady: boolean;

  removeValue?: () => void;

  /**
   * If true, the user will be shown as logged out if the token is not ready and we can't get it from somewhere
   */
  isRequired: boolean;

  isExpired: boolean;

  /**
   * Determines if the token is close to needing refreshing - for example, if it expires in less than 60 seconds
   */
  shouldRefresh: boolean;

  updateWith(dependencies: TokenDependencies): Promise<IAuthTokenError | null>;

  /**
   * Can be used to directly update this value EG when we complete a login
   * @param data
   */
  update(data: IAuthTokenData): void;

  checkResponse(
    response: Response,
    body?: string,
  ): Promise<IAuthTokenError | null>;

  logoutCallback?: () => Promise<void>;

  /**
   * Should return a list of dependencies that can be used to refresh this token. If any of the dependencies are
   * valid, they'll be used to refresh this token.
   */
  dependencies: AuthTokenSource[];

  /**
   * Can be used client-side to write the current token values to cookies which can then be read back by the server
   * in future requests. This should be called after each time the cookie is updated
   */
  saveCookies(writeableCookies: WriteableServerCookies): void;

  /**
   * Allows the token to be timed out if we tried to refresh it and failed.
   *
   * This is used to generate an exponential backoff when trying to refresh the token so we don't just spam our servers
   * with reconnection attempts.
   */
  timeoutExpiry: DateTime | null;

  canAttemptAgain: boolean;
  /**
   * Generate a list of cookie names that this token uses - these will be deleted when the user logs out
   */
  usedCookieNames: string[];
}

export interface IAuthTokens {
  serialized: ISerializedAuthTokens;
  saveCookies(writeableCookies: WriteableServerCookies): void;
  isPendingRefresh: boolean;
  isLoggedIn: boolean;

  /**
   * Will attempt to get the token from the store, and if it's not there, will attempt to get a new one
   *
   * Will also attempt to get
   * @param from Where to get the token from
   * @param forceRefetch If true, will attempt to refetch the token even if we have a valid one already
   */
  getAccessToken(
    from: AuthTokenSource,
    forceRefetch?: boolean,
  ): Promise<string | IAuthTokenError>;

  /**
   * Use me to postprocess the response from a fetch request.
   *
   * If the response indicated that we weren't authorized, this will return an action to take.
   *
   * @param source Where you got the token.
   * @param response The response from the fetch request
   * @param body The body from the response - we have to pass that separately to prevent trying to parse it twice
   * @returns An action to take - one of RETRY, LOGOUT, SHOW_ERROR or SUCCESS_CONTINUE
   */
  checkResponse(
    source: AuthTokenSource,
    response: Response,
    body?: string,
  ): Promise<AuthTokenErrorAction>;

  ensureAllTokensAreReady(): Promise<void>;

  /**
   * Can be used to insert an initial token supplied by a user. THis can then be used to refresh the other tokens if
   * needed
   * @param source Where we got the token
   * @param value The token value
   * @param expiryDate The expiry date of the token,in ISO format
   */
  bootstrapWith(
    source: AuthTokenSource,
    value: string,
    expiryDate?: string,
  ): void;

  /**
   * Performs a logout operation with all token as needed
   *
   * Will return a list of cookies that should be deleted
   */
  logout(): Promise<string[]>;
  /**
   * Will return the last known token value - this will not refresh the token automatically. To refresh, you must use
   * ensureAllTokensAreReady periodically.
   */
  lastTokenValues: Record<AuthTokenSource, string | null>;

  getSourceMeta<T>(source: AuthTokenSource): T | null;
}

export type TokenDebuggingEventAction =
  | 'loadedFromCookie'
  | 'loadedDuringHydration'
  | 'fetched'
  | 'updated'
  | 'fetchAttempt'
  | 'updateAttempt'
  | 'fetchFailed'
  | 'updateFailed'
  | 'timedOut'
  | 'timeoutEnded'
  | 'createdEmpty'
  | 'logoutFailed'
  | 'logoutSucceeded';

export interface TokenDebuggingEvent {
  action: TokenDebuggingEventAction;
  message?: string;
  error?: Error;
}

export type GlobalTokenDebuggingEventAction =
  | 'tokenStoreCreated'
  | 'tokenStoreDestroyed'
  | 'ensureAllTokensAreReadyStarted'
  | 'ensureAllTokensAreReadyFinished'
  | 'deletedCookies'
  | 'logoutFailed'
  | 'logoutSucceeded';
export interface GlobalTokenDebuggingEvent {
  action: GlobalTokenDebuggingEventAction;
  message?: string;
  error?: Error;
}

export interface ISourceTokenDebugger {
  addEvent(event: TokenDebuggingEvent): void;
}

export interface ITokenDebugger {
  addEvent(source: AuthTokenSource, event: TokenDebuggingEvent): void;

  forSource(source: AuthTokenSource): ISourceTokenDebugger;

  addGlobalEvent(event: GlobalTokenDebuggingEvent): void;
}

export interface IAuthTokenOptions {
  wallClock?: IWallClock;
  debugger?: ITokenDebugger;
}

export interface IAuthTokenFactory<Meta = unknown> {
  createOnServer(
    cookies: ServerCookies,
    options?: IAuthTokenOptions,
  ): IAuthTokenStore<Meta> | undefined;

  createOnClient(
    fromServer: ISerializedAuthToken,
    options?: IAuthTokenOptions,
  ): IAuthTokenStore<Meta>;

  createEmpty(options?: IAuthTokenOptions): IAuthTokenStore<Meta>;
}

/**
 * Can be used to generate auth tokens depending on where they are being used.
 */
export interface IAuthTokensFactory {
  createOnServer(
    cookies: ServerCookies,
    options?: IAuthTokenOptions,
  ): IAuthTokens;

  createOnClient(
    fromServer: ISerializedAuthTokens,
    options?: IAuthTokenOptions,
  ): IAuthTokens;
}
